import AddIcon from '@mui/icons-material/AddCircleOutline';
import CheckedIcon from '@mui/icons-material/CheckBox';
import UncheckedIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import EditIcon from '@mui/icons-material/EditOutlined';
import { Autocomplete, Checkbox, TextField, Tooltip } from '@mui/material';
import useColorsStyles from 'common/colors';
import CustomAvatar from 'common/components/CustomAvatar';
import useFlexBoxStyles from 'common/flexBox';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import ChannelsChipRender from './ChannelsChipRender';

const useChannelsStyles = makeStyles()((theme) => ({
    root: {
        '& .MuiInputBase-root': {
            background: '#FFFF',
            borderRadius: theme.spacing(2),
            [theme.breakpoints.up('xl')]: {
                height: 'auto',
                borderRadius: theme.spacing(2.5),
            },

            '& > fieldset': {
                borderColor: '#E2E2E2',
            },

            '& > input': {
                padding: theme.spacing(0, 1),
                height: '100%',
                fontSize: '12px',
                fontWeight: '500',
                position: 'relative',
                '&:focus': {
                    borderRadius: 5,
                },
                [theme.breakpoints.up('xl')]: {
                    fontSize: '14px',
                },
            },
        },
    },
    deactivated: {
        position: 'absolute',
        width: theme.spacing(2),
        height: theme.spacing(2),
        color: theme.palette.common.red,
        background: theme.palette.common.white,
        left: theme.spacing(-0.5),
        bottom: theme.spacing(-0.5),
        borderRadius: '50%',
    },
}));

export const ChannelsAutocomplete = ({ channelsIds, onChange, channels, loading, hidePopupIcon }) => {
    const { classes, cx } = useChannelsStyles();
    const { classes: flexBox } = useFlexBoxStyles();
    const { classes: colors } = useColorsStyles();
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    const [channelIdOptions, channelsHash] = useMemo(() => {
        const hash = channels?.reduce((hash, ch) => (hash[ch.id] = ch) && hash, {});
        return hash ? [Object.keys(hash), hash] : [[], {}];
    }, [channels]);

    const getPopupIcon = () => {
        if (!open) {
            return channelsIds?.length ? <EditIcon size="small" /> : <AddIcon size="small" />;
        }
        return null;
    };

    return (
        <Autocomplete
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            loading={loading}
            className={classes.root}
            loadingText={t('features.streaming.loading')}
            disableCloseOnSelect
            multiple
            options={channelIdOptions}
            value={channelsIds || []}
            filterOptions={(options, state) =>
                options.filter((option) => {
                    const channel = channelsHash[option];
                    return (
                        channel &&
                        (channel.name.toLowerCase().trim().includes(state.inputValue.toLowerCase().trim()) ||
                            channel.description.toLowerCase().trim().includes(state.inputValue.toLowerCase().trim()))
                    );
                })
            }
            renderTags={(value, getTagProps) => (
                <div className={cx(flexBox.flex, flexBox.gap1)}>
                    {value.map((option, index) => {
                        const props = getTagProps({ index });
                        const channel = channelsHash[option] || { id: option };
                        return (
                            <ChannelsChipRender
                                key={index}
                                props={props}
                                loading={loading}
                                channel={channel}
                                index={index}
                            />
                        );
                    })}
                </div>
            )}
            getOptionDisabled={(option) => !channelsHash[option]?.activated}
            forcePopupIcon={!hidePopupIcon}
            popupIcon={getPopupIcon()}
            renderOption={(props, option, { selected }) => {
                const channel = channelsHash[option] || { id: option };
                if (!channel.activated) {
                    return null;
                } else {
                    return (
                        <li
                            aria-hidden="true"
                            {...props}
                            className={flexBox.padding01}
                            onClick={channel.activated || selected ? props.onClick : undefined}
                        >
                            <Tooltip title={channel.description} disableHoverListener={!channel} leaveDelay={0}>
                                <div
                                    className={cx(
                                        flexBox.flex,
                                        flexBox.justifyContentEnd,
                                        flexBox.alignItemsCenter,
                                        flexBox.gap1,
                                        !channel.activated && colors.bgLightGray,
                                        (channel.activated || selected) && flexBox.cursorPointer,
                                    )}
                                >
                                    <CustomAvatar icon={channel.icon} name={channel.name} id={channel.id} />
                                    {channel.name || 'unknown'}
                                    <div className={flexBox.grow1} />
                                    <Checkbox
                                        icon={<UncheckedIcon fontSize="small" />}
                                        checkedIcon={<CheckedIcon fontSize="small" />}
                                        disabled={!selected && !channel.activated}
                                        checked={selected}
                                    />
                                </div>
                            </Tooltip>
                        </li>
                    );
                }
            }}
            disableClearable={true}
            renderInput={(params) => {
                if (!params.inputProps) {
                    params.inputProps = {};
                }
                params.inputProps.className = cx(params.inputProps.className, flexBox.cursorPointer);
                return (
                    <TextField
                        {...params}
                        variant="outlined"
                        placeholder={channelsIds?.length ? '' : t('common.channels.channelsTransmission')}
                    />
                );
            }}
            onChange={(_, labels) => onChange?.(labels)}
            autoCorrect="false"
        />
    );
};

export default ChannelsAutocomplete;
